import type { CSSProperties, MouseEvent } from 'react'
import { useState } from 'react'

interface ImageMagnifierProps {
    showZoom: boolean;
    src?: string;
    className?: string;
    width?: string | number;
    height?: string | number;
    alt?: string;
    magnifierHeight?: number;
    magnifierWidth?: number;
    zoomLevel?: number;
    objectFit?: CSSProperties['objectFit'];
    degrees?: number
    scale?: number
    scaleImage?: () => void
}

const ImageMagnifier = ({
    showZoom,
    src,
    className,
    width,
    height,
    alt,
    magnifierHeight = 200,
    magnifierWidth = 300,
    zoomLevel = 4,
    objectFit,
    degrees,
    scale,
    scaleImage,
}: ImageMagnifierProps) => {
    const [showMagnifier, setShowMagnifier] = useState<boolean>(false);
    const [[imgWidth, imgHeight], setSize] = useState<[number, number]>([0, 0]);
    const [[x, y], setXY] = useState<[number, number]>([0, 0]);

    const mouseEnter = (e: MouseEvent<HTMLImageElement>) => {
        const el = e.currentTarget;

        const { width, height } = el.getBoundingClientRect();
        setSize([width, height]);
        setShowMagnifier(true);
    }

    const mouseLeave = (e: MouseEvent<HTMLImageElement>) => {
        e.preventDefault();
        setShowMagnifier(false);
    }

    const mouseMove = (e: MouseEvent<HTMLImageElement>) => {
        const el = e.currentTarget;
        const { top, left, width, height } = el.getBoundingClientRect();

        let x = e.pageX - left - window.scrollX
        let y = e.pageY - top - window.scrollY
        const currentY = y
        const currentX = x

        // Invert coordinates for 180 degree rotation
        switch (degrees) {
            case 90:
                x = currentY
                y = width - currentX
                break
            case 180:
                x = width - x
                y = height - y
                break
            case 270:
                x = height - currentY
                y = currentX
                break
        }

        setXY([x * (1 / (scale ?? 1)), y * (1 / (scale ?? 1))]);
    };

    return <div style={{ objectFit: objectFit, width: '100%', height: '100%', overflow: 'hidden' }}>
        <img
            style={{
              width: width,
              height: height,
                objectFit: objectFit,
            } as CSSProperties}
            src={src}
            className={className}
            alt={alt}
            onMouseEnter={mouseEnter}
            onMouseLeave={mouseLeave}
            onMouseMove={mouseMove}
            loading='lazy'
            onLoad={scaleImage}
        />
        {showZoom && <div
            style={{
                display: showMagnifier ? '' : 'none',
                position: 'absolute',
                pointerEvents: 'none',
                height: `${magnifierHeight}px`,
                width: `${magnifierWidth}px`,
                opacity: '1',
                border: '1px solid lightgrey',
                backgroundColor: 'white',
                borderRadius: '5px',
                backgroundImage: `url('${src}')`,
                backgroundRepeat: 'no-repeat',
                top: `${y - magnifierHeight / 2}px`,
                left: `${x - magnifierWidth / 2}px`,
                backgroundSize: `${degrees == 90 || degrees == 270 ? imgHeight * zoomLevel : imgWidth * zoomLevel}px ${degrees == 90 || degrees == 270 ? imgWidth * zoomLevel : imgHeight * zoomLevel}px`,
                backgroundPositionX: `${(-x * zoomLevel + magnifierWidth / 2) * (scale ?? 1)}px`,
                backgroundPositionY: `${(-y * zoomLevel + magnifierHeight / 2) * (scale ?? 1)}px`,
            } as CSSProperties}
        />}
    </div>
};

export default ImageMagnifier;